import { setFilterParams } from '@/utils/list-filter-params';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

// REQ-7/REQ-8 refactor pass: the Customers, Orders and Subscriptions pages each wrote this same
// debounce-then-commit-to-the-URL shape out in full for their own free-text search box — one hook
// instead (react-base/RULES.md § Hooks: a custom hook composes other hooks, which this one does —
// useState, useEffect and useSearchParams — so it earns being a hook rather than being copied a
// fourth time).
//
// Returns the raw, instantly-typed value (component state, since it is UI-only until the debounce
// below commits it — react-base/RULES.md § State management) and its setter. The caller still
// derives the actually-applied value itself, from the URL, via list-filter-params.ts's own
// parseStringParam, the same way every list page already does for its other filters; `appliedValue`
// is only how this hook knows its own last commit has landed, so it can stop scheduling another
// one — skipping that (true on mount, and right after a commit) is what stops an unconditional
// write 300ms after every mount from resetting a deep-linked page number with no user interaction,
// and from racing a filter change made in that same window.
export const useDebouncedFilterParam = (key: string, pageKey: string, appliedValue: string) => {
  const [, setSearchParams] = useSearchParams();
  const [value, setValue] = useState(appliedValue);

  useEffect(() => {
    if (value === appliedValue) return;
    const timer = setTimeout(() => {
      setSearchParams(prev => setFilterParams(prev, { [key]: value || null }, pageKey), {
        replace: true
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [value, appliedValue, key, pageKey, setSearchParams]);

  return [value, setValue] as const;
};
